import { useMemo } from "react";

interface Props {
  subject: string;
  body: string;
}

interface EtiquetteHint {
  id: string;
  icon: string;
  color: string;
  message: string;
  type: "warn" | "tip" | "ok";
}

function analyzeEmail(subject: string, body: string): EtiquetteHint[] {
  const hints: EtiquetteHint[] = [];
  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();

  // 제목 분석
  if (trimmedSubject.length === 0) {
    hints.push({ id: "no_subject", icon: "ri-error-warning-line", color: "#EF4444", message: "제목을 입력해 주세요. 제목 없는 이메일은 스팸으로 처리될 수 있습니다.", type: "warn" });
  } else if (trimmedSubject.length < 5) {
    hints.push({ id: "short_subject", icon: "ri-error-warning-line", color: "#F59E0B", message: "제목이 너무 짧습니다. 받는 사람이 내용을 예측할 수 있게 구체적으로 써주세요.", type: "warn" });
  } else if (trimmedSubject.length > 60) {
    hints.push({ id: "long_subject", icon: "ri-alert-line", color: "#F59E0B", message: "제목이 60자를 넘으면 일부 메일 클라이언트에서 잘립니다. 핵심만 남겨보세요.", type: "warn" });
  } else {
    hints.push({ id: "subject_ok", icon: "ri-checkbox-circle-line", color: "#10B981", message: "제목 길이가 적절합니다.", type: "ok" });
  }

  // 인사말 분석
  const hasGreeting = /안녕하세요|감사합니다|수고하십니다|담당자|귀하/.test(trimmedBody);
  if (trimmedBody.length > 30 && !hasGreeting) {
    hints.push({ id: "no_greeting", icon: "ri-user-voice-line", color: "#F59E0B", message: "인사말이 없습니다. '안녕하세요, ○○○입니다.'로 시작하면 더 좋은 인상을 줍니다.", type: "warn" });
  }

  // 마무리 인사 분석
  const hasClosing = /감사합니다|드립니다|부탁드립니다|확인 부탁|바랍니다/.test(trimmedBody);
  if (trimmedBody.length > 50 && !hasClosing) {
    hints.push({ id: "no_closing", icon: "ri-chat-check-line", color: "#F59E0B", message: "마무리 인사가 없습니다. '검토해 주시면 감사하겠습니다.' 등으로 마무리하세요.", type: "warn" });
  }

  // 본문 너무 짧음
  if (trimmedBody.length > 0 && trimmedBody.length < 20) {
    hints.push({ id: "too_short", icon: "ri-edit-line", color: "#6B7280", message: "본문이 너무 짧습니다. 메일의 목적과 요청사항을 명확히 적어주세요.", type: "tip" });
  }

  // 긴급/급함 단어
  if (/급합니다|빨리|당장|즉시/.test(trimmedBody)) {
    hints.push({ id: "urgent_word", icon: "ri-time-line", color: "#6B7280", message: "긴급 표현이 있습니다. 실제로 긴급한 경우 제목에 [긴급]을 붙이면 더 효과적입니다.", type: "tip" });
  }

  // 모호한 표현
  if (/것 같아요|아닐까요|어떨까요/.test(trimmedBody)) {
    hints.push({ id: "vague_expression", icon: "ri-question-line", color: "#F59E0B", message: "모호한 표현(~것 같아요, ~아닐까요)보다 명확한 표현이 신뢰감을 줍니다.", type: "warn" });
  }

  // 전체 길이 OK
  if (trimmedBody.length >= 50 && hasGreeting && hasClosing && hints.filter(h => h.type === "warn").length === 0) {
    hints.push({ id: "great", icon: "ri-award-line", color: "#10B981", message: "비즈니스 이메일 형식이 잘 갖춰졌습니다! 인사말, 본론, 마무리가 모두 있습니다.", type: "ok" });
  }

  return hints.filter(h => h.type !== "ok" || hints.filter(x => x.type !== "ok").length === 0).slice(0, 4);
}

export default function EtiquetteCoach({ subject, body }: Props) {
  const hints = useMemo(() => analyzeEmail(subject, body), [subject, body]);

  const hasContent = subject.trim().length > 0 || body.trim().length > 0;
  if (!hasContent) return null;

  return (
    <div className="border border-amber-500/15 bg-amber-500/[0.04] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-amber-500/10 flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-medal-line text-amber-400/70 text-xs"></i>
        </div>
        <span className="text-amber-400/60 text-[10px] font-semibold uppercase tracking-wider">비즈니스 예절 코치</span>
      </div>

      <div className="px-3 py-2 space-y-1.5">
        {hints.map(hint => (
          <div key={hint.id} className="flex items-start gap-2">
            <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0 mt-px">
              <i className={`${hint.icon} text-[11px]`} style={{ color: hint.color }}></i>
            </div>
            <p className="text-[#888888] text-[11px] leading-relaxed">{hint.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
